import React, { forwardRef, KeyboardEvent, useState } from 'react';
import { clsx } from 'clsx';
import { IconType } from 'react-icons';
import { Required } from './Required';
import { FaRegEye, FaRegEyeSlash } from 'react-icons/fa6';

interface DataAttributes {
  'data-tooltip-id'?: string;
  'data-tooltip-content'?: string;
  'data-tooltip-place'?: string;
}

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  className?: string;
  fontSize?: string;
  IconEnd?: IconType;
  IconStart?: IconType;
  ButtonStart?: React.ReactNode;
  ButtonEnd?: React.ReactNode;
  inputSize?: 'sm' | 'default';
  small?: boolean;
  width?: string;
  tooltipConfig?: DataAttributes;
  inputType?: 'SELECT' | 'INPUT';
  label?: string | React.ReactNode;
  classNameLabel?: string;
  showRequired?: boolean;
  someDescription?: React.ReactNode;
  error?: string | string[] | null;
}

const BASE_INPUT_CLASSES =
  'block bg-white m-0 w-full overflow-hidden text-zinc-800 font-normal rounded px-3 py-1 border border-gray-300 placeholder:text-gray-400 placeholder:font-normal focus:border-blue-400 sm:leading-6 outline-none placeholder:transition-all focus:placeholder:opacity-60 placeholder:text-xs bg-transparent dark:bg-transparent dark:border-zinc-600 focus:placeholder:translate-x-1 dark:text-zinc-400 disabled:bg-gray-100 disabled:dark:bg-zinc-950 dark:disabled:text-gray-400 read-only:bg-gray-100 read-only:dark:bg-zinc-950 dark:read-only:text-gray-400';

const ICON_BASE_CLASSES = 'text-gray-400 absolute top-0 h-full !text-lg z-10';

export const InputPassword = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      fontSize = 'text-sm',
      IconEnd,
      IconStart,
      ButtonStart,
      ButtonEnd,
      width,
      tooltipConfig = {},
      inputType = 'INPUT',
      onKeyDown,
      small = false,
      label,
      classNameLabel,
      showRequired,
      someDescription,
      error,
      ...props
    },
    ref,
  ) => {
    const paddingClasses = clsx({
      'ps-7': !!IconStart && !small,
      'pe-7': !!IconEnd && !small,
      '!ps-5': !!IconStart && small,
      '!pe-5': !!IconEnd && small,
    });

    const borderClasses = clsx({
      'rounded-l-none !border-l-0': !!ButtonStart,
      'rounded-r-none !border-r-0': !!ButtonEnd,
    });

    const cursorClass =
      inputType === 'SELECT' ? 'read-only:cursor-pointer' : 'read-only:cursor-default';

    const inputBaseClass = clsx(
      BASE_INPUT_CLASSES,
      paddingClasses,
      borderClasses,
      cursorClass,
      className,
    );

    const containerClasses = clsx('flex h-full', small && 'py-0', !!label && 'flex-col gap-0');
    const wrapperClasses = clsx('relative w-full h-full', small && 'py-0');

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
      onKeyDown?.(e);
    };

    const mergedProps = {
      ...props,
      ...tooltipConfig,
    } as React.InputHTMLAttributes<HTMLInputElement>;

    const [showPassword, setShowPassword] = useState(false);

    return (
      <div className={containerClasses} style={width ? { width } : undefined}>
        {!!label && (
          <label htmlFor={props.id} className={clsx('text-xs', classNameLabel)}>
            {label} {showRequired && <Required />}{' '}
            {!!someDescription && <span className="text-xs text-slate-400">{someDescription}</span>}
          </label>
        )}
        <div className="flex h-full w-full">
          <div className={clsx(wrapperClasses, className, 'relative')}>
            <input
              ref={ref}
              type={showPassword ? 'text' : 'password'}
              className={clsx(inputBaseClass, fontSize, small ? '!px-2.5 !py-0' : '')}
              onKeyDown={handleKeyDown}
              {...mergedProps}
            />
            {!showPassword && (
              <FaRegEye
                className={clsx(ICON_BASE_CLASSES, 'right-1.5 cursor-pointer hover:text-slate-500')}
                onClick={() => setShowPassword((prev) => !prev)}
              />
            )}
            {showPassword && (
              <FaRegEyeSlash
                className={clsx(ICON_BASE_CLASSES, 'right-1.5 cursor-pointer hover:text-slate-500')}
                onClick={() => setShowPassword((prev) => !prev)}
              />
            )}
          </div>
          {ButtonEnd && ButtonEnd}
        </div>
        {!!error && <small className="w-100 block text-xs italic text-red-500">{error}</small>}
      </div>
    );
  },
);

InputPassword.displayName = 'SuperInputPassword';
